import { ShortcutAction } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutAction';
import { ShortcutSection } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutSection';

type ShortcutDefinition = {
    description: string;
    shortcut: string;
};

type ShortcutGroup = {
    label?: string;
    shortcuts: readonly ShortcutDefinition[];
};

export type ShortcutSectionDefinition = {
    groups: readonly ShortcutGroup[];
    labelKey:
        | 'labelCameraShortcuts'
        | 'labelPoiShortcuts'
        | 'labelArticleShortcuts'
        | 'labelBoxSelectionShortcuts'
        | 'labelPlacementShortcuts';
    notes?: readonly string[];
    shortcutWidthClassName: string;
};

export const shortcutList: Record<ShortcutSection, ShortcutSectionDefinition> = {
    [ShortcutSection.CAMERA]: {
        labelKey: 'labelCameraShortcuts',
        shortcutWidthClassName: 'min-w-32',
        groups: [
            {
                shortcuts: [
                    { description: 'Rotate camera', shortcut: ShortcutAction.RotateCamera },
                    { description: 'Move camera', shortcut: `${ShortcutAction.MiddleMouseButton} pressed` },
                    { description: 'Zoom camera', shortcut: `${ShortcutAction.MiddleMouseButton} scroll` },
                    { description: 'Lock camera', shortcut: ShortcutAction.LockView },
                    { description: 'Unlock camera', shortcut: ShortcutAction.UnlockView },
                    { description: 'Center camera', shortcut: ShortcutAction.FocusBuilding }
                ]
            }
        ],
        notes: ['MMB = Middle Mouse Button']
    },
    [ShortcutSection.POI]: {
        labelKey: 'labelPoiShortcuts',
        shortcutWidthClassName: 'min-w-32',
        groups: [
            {
                shortcuts: [
                    { description: 'Activate POI navigation mode', shortcut: ShortcutAction.PoiNavigationMode },
                    {
                        description: 'Deactivate POI navigation mode',
                        shortcut: `${ShortcutAction.PoiNavigationMode} or ${ShortcutAction.Cancellation}`
                    },
                    {
                        description: 'POI selection with camera re-alignment',
                        shortcut: `${ShortcutAction.LeftMouseButton} 2x`
                    },
                    {
                        description: 'POI selection without camera re-alignment',
                        shortcut: ShortcutAction.LeftMouseButton
                    }
                ]
            },
            {
                label: 'Quick navigation',
                shortcuts: [
                    { description: 'Temporary POI navigation', shortcut: ShortcutAction.TemporaryPoiNavigation },
                    {
                        description: 'Switch to previous POI',
                        shortcut: ShortcutAction.Previous
                    },
                    {
                        description: 'Switch to next POI',
                        shortcut: ShortcutAction.Next
                    },
                    { description: 'Switch POI type', shortcut: ShortcutAction.SwitchPoiType },
                    {
                        description: 'Switch POI type (reversed direction)',
                        shortcut: ShortcutAction.SwitchPoiTypeReverse
                    },
                    {
                        description: 'Toggle camera re-alignment',
                        shortcut: ShortcutAction.ToggleCameraRealignment
                    }
                ]
            }
        ],
        notes: ['LMB = Left Mouse Button']
    },
    [ShortcutSection.ARTICLE]: {
        labelKey: 'labelArticleShortcuts',
        shortcutWidthClassName: 'min-w-32',
        groups: [
            {
                label: 'Selection',
                shortcuts: [
                    { description: 'Select article', shortcut: ShortcutAction.LeftMouseButton },
                    { description: 'Select several articles', shortcut: ShortcutAction.MultiSelection },
                    { description: 'Select all articles', shortcut: ShortcutAction.SelectAll },
                    { description: 'Copy active selection', shortcut: ShortcutAction.Copy },
                    { description: 'Cut active selection', shortcut: ShortcutAction.Cut },
                    { description: 'Paste active selection', shortcut: ShortcutAction.Paste },
                    { description: 'Discard active selection', shortcut: ShortcutAction.Cancellation },
                    { description: 'Delete active selection', shortcut: ShortcutAction.Delete },
                    { description: 'Show context menu', shortcut: ShortcutAction.RightMouseButton }
                ]
            },
            {
                label: 'Transformation',
                shortcuts: [
                    {
                        description: 'Move article with snapping',
                        shortcut: `${ShortcutAction.LeftMouseButton} pressed`
                    },
                    {
                        description: 'Move article without snapping',
                        shortcut: `Alt + ${ShortcutAction.LeftMouseButton} pressed`
                    },
                    { description: 'Rotate article (90 degrees)', shortcut: ShortcutAction.Rotate },
                    { description: 'Show transformation gizmo', shortcut: ShortcutAction.Transform },
                    { description: 'Discard active transformation', shortcut: ShortcutAction.Cancellation }
                ]
            },
            {
                label: 'Grouping',
                shortcuts: [
                    { description: 'Create/Edit group', shortcut: ShortcutAction.Group },
                    { description: 'Revert group', shortcut: ShortcutAction.Ungroup }
                ]
            }
        ],
        notes: ['LMB = Left Mouse Button', 'RMB = Right Mouse Button']
    },
    [ShortcutSection.SELECTION]: {
        labelKey: 'labelBoxSelectionShortcuts',
        shortcutWidthClassName: 'min-w-36',
        groups: [
            {
                shortcuts: [
                    { description: 'Select articles from active POI', shortcut: ShortcutAction.LeftMouseButton },
                    { description: 'Select articles recursively', shortcut: ShortcutAction.MultiSelection },
                    {
                        description: 'Select fully enclosed articles',
                        shortcut: `${ShortcutAction.LeftMouseButton} (Left to Right)`
                    },
                    {
                        description: 'Select partially enclosed articles',
                        shortcut: `${ShortcutAction.LeftMouseButton} (Right to Left)`
                    },
                    { description: 'Discard selection', shortcut: ShortcutAction.Cancellation }
                ]
            }
        ],
        notes: ['LMB = Left Mouse Button']
    },
    [ShortcutSection.PLACEMENT]: {
        labelKey: 'labelPlacementShortcuts',
        shortcutWidthClassName: 'min-w-44',
        groups: [
            {
                shortcuts: [
                    { description: 'Select placement type', shortcut: ShortcutAction.NumberKey },
                    { description: 'Execute placement button', shortcut: ShortcutAction.Execute },
                    { description: 'Navigate through solutions list', shortcut: ShortcutAction.ListNavigation },
                    { description: 'Apply selected solution', shortcut: ShortcutAction.Execute },
                    { description: 'Navigate through article table', shortcut: ShortcutAction.ListNavigation }
                ]
            }
        ]
    }
};
